import { z } from 'zod';

import { apiSuccess, apiList, parseApiResponse } from '@services/apiSchemas';

import operatorApiFetch from './apiService';

// ============================================================
// Schemas — 對齊 app/controllers/api/operator/v1/inbounds_controller.rb
// 與 inbound_value_added_services_controller.rb
// ============================================================

// `index` (GET /inbounds) — fetchList 端點，render_data_list 的 block
const InboundListItemSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  barcode: z.string().nullable(),
  merchant_id: z.string(),
  merchant_name: z.string().nullable(),
  status: z.string(),
  inbound_type: z.string().nullable(),
  eta: z.string().nullable(),
  supplier_id: z.string().nullable().optional(),
  supplier: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
  extra_name: z.string().nullable(),
  total_items: z.number(),
  scheduled_quantity: z.number(),
  putaway_quantity: z.number(),
  received_quantity: z.number(),
  arrived_at: z.string().nullable(),
  received_at: z.string().nullable(),
  putaway_at: z.string().nullable(),
  created_at: z.string(),
}).passthrough();

// `get_records` — table 用 row（與 index shape 接近，但無 barcode）
const InboundTableRowSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  merchant_id: z.string(),
  merchant_name: z.string().nullable(),
  status: z.string(),
  inbound_type: z.string().nullable(),
  eta: z.string().nullable(),
  supplier_id: z.string().nullable().optional(),
  supplier: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
  extra_name: z.string().nullable(),
  total_items: z.number(),
  scheduled_quantity: z.number(),
  putaway_quantity: z.number(),
  received_quantity: z.number(),
  arrived_at: z.string().nullable(),
  received_at: z.string().nullable(),
  putaway_at: z.string().nullable(),
  created_at: z.string(),
}).passthrough();

// `show` endpoint nested shapes
const InboundSerialNumberRefSchema = z.object({
  product_serial_number_id: z.string(),
  created_at: z.string(),
}).passthrough();

const InboundReceiptItemRefSchema = z.object({
  id: z.string(),
  batch: z.string().nullable(),
  expiration_date: z.string().nullable(),
  manufacturing_date: z.string().nullable(),
  custom_attributes: z.record(z.string(), z.unknown()).nullable(),
  quantity: z.number().nullable(),
  box_count: z.number().nullable(),
  quantity_per_box: z.number().nullable(),
  loose_quantity: z.number().nullable(),
  putaway_quantity: z.number().nullable(),
  received_at: z.string().nullable(),
  putaway_at: z.string().nullable(),
  verifier_name: z.string().nullable(),
  note: z.string().nullable(),
}).passthrough();

const InboundItemDetailSchema = z.object({
  id: z.string(),
  product_id: z.string(),
  product_name: z.string().nullable(),
  product_sku: z.string().nullable(),
  product_barcode: z.string().nullable(),
  product_image_url: z.string().nullable(),
  product_validity_days: z.number().nullable(),
  batch_management_required: z.boolean().nullable(),
  manufacturing_date_required: z.boolean().nullable(),
  required_serial_numbers_count: z.number().nullable(),
  serial_number_pattern: z.string().nullable(),
  box_serial_number_delimiter: z.string().nullable(),
  small_quantity_per_large_unit: z.number().nullable(),
  inventory_type_id: z.string().nullable(),
  inventory_type_name: z.string().nullable(),
  batch: z.string().nullable(),
  expiration_date: z.string().nullable(),
  manufacturing_date: z.string().nullable(),
  custom_attributes: z.record(z.string(), z.unknown()).nullable(),
  unplanned: z.boolean(),
  scheduled_quantity: z.number().nullable(),
  putaway_quantity: z.number().nullable(),
  received_quantity: z.number().nullable(),
  received_at: z.string().nullable(),
  received_box_count: z.number().nullable(),
  received_quantity_per_box: z.number().nullable(),
  received_loose_quantity: z.number().nullable(),
  serial_numbers: z.array(InboundSerialNumberRefSchema),
  receipt_items: z.array(InboundReceiptItemRefSchema),
}).passthrough();

const InboundReceiptItemFullSchema = z.object({
  id: z.string(),
  inbound_item_id: z.string().nullable(),
  unplanned: z.boolean(),
  product_id: z.string().nullable(),
  product_name: z.string().nullable(),
  product_sku: z.string().nullable(),
  inventory_type_name: z.string().nullable(),
  batch: z.string().nullable(),
  expiration_date: z.string().nullable(),
  manufacturing_date: z.string().nullable(),
  custom_attributes: z.record(z.string(), z.unknown()).nullable(),
  quantity: z.number().nullable(),
  box_count: z.number().nullable(),
  quantity_per_box: z.number().nullable(),
  loose_quantity: z.number().nullable(),
  putaway_quantity: z.number().nullable(),
  received_at: z.string().nullable(),
  putaway_at: z.string().nullable(),
  verifier_name: z.string().nullable(),
  note: z.string().nullable(),
}).passthrough();

const InboundReceiptSummarySchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  status: z.string(),
  total_items: z.number(),
  total_received_quantity: z.number(),
  received_at: z.string().nullable(),
  auto_putaway_available: z.boolean(),
  created_at: z.string(),
  receipt_items: z.array(InboundReceiptItemFullSchema),
}).passthrough();

const InboundHistorySchema = z.object({
  id: z.string(),
  description: z.unknown(),
  operator_name: z.string().nullable(),
  user_name: z.string().nullable(),
  created_at: z.string(),
}).passthrough();

const InboundVasEntrySchema = z.object({
  id: z.string(),
  merchant_value_added_service_id: z.string().nullable(),
  title: z.string(),
  quantity: z.number().nullable(),
  price: z.number().nullable(),
  created_at: z.string().optional(),
  subtotal: z.number().optional(),
}).passthrough();

const InboundReturnOrderRefSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  status: z.string(),
  created_at: z.string(),
}).passthrough();

const InboundActiveReceiptSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  item_progress: z.object({
    scanned: z.number(),
    expected: z.number(),
  }).passthrough(),
  created_at: z.string(),
}).passthrough();

// `show` (GET /inbounds/:id) — render_data_item 大 payload
const InboundDetailSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  barcode: z.string().nullable(),
  merchant_id: z.string(),
  merchant_name: z.string().nullable(),
  status: z.string(),
  inbound_type: z.string().nullable(),
  eta: z.string().nullable(),
  supplier_id: z.string().nullable().optional(),
  supplier: z.object({ id: z.string(), name: z.string() }).nullable().optional(),
  extra_name: z.string().nullable(),
  note: z.string().nullable(),
  arrived_at: z.string().nullable(),
  received_at: z.string().nullable(),
  putaway_at: z.string().nullable(),
  created_at: z.string(),
  auto_putaway: z.boolean(),
  editable: z.boolean(),
  cancelable: z.boolean(),
  resumable: z.boolean(),
  active_receipt: InboundActiveReceiptSchema.nullable(),
  items: z.array(InboundItemDetailSchema),
  receipts: z.array(InboundReceiptSummarySchema),
  histories: z.array(InboundHistorySchema),
  value_added_services: z.array(InboundVasEntrySchema),
  return_orders: z.array(InboundReturnOrderRefSchema),
}).passthrough();

// `create` / `update` / `cancel` / `resume` 回傳 render_data_item(inbound)
// — inbound AR 物件直接走 as_json，shape 是 inbounds 表的所有欄位。
// 這裡只標核心欄位，其餘走 passthrough。
const InboundBasicSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  status: z.string(),
  merchant_id: z.string(),
}).passthrough();

// `close_receiving` — render_data_item({ id, status })
const CloseReceivingResultSchema = z.object({
  id: z.string(),
  status: z.string(),
}).passthrough();

// `export` / `generate_pdf` / `generate_labels` — render_data_item({ job_id })
const JobIdResultSchema = z.object({
  job_id: z.string(),
}).passthrough();

// `import` — render_data_item({ job_id, import_id })
const ImportResultSchema = z.object({
  job_id: z.string(),
  import_id: z.string(),
}).passthrough();

// 加值服務 — serialize_service 回 { id, merchant_value_added_service_id,
// title, price, quantity, subtotal }
const VasServiceResultSchema = z.object({
  id: z.string(),
  merchant_value_added_service_id: z.string().nullable(),
  title: z.string(),
  price: z.number().nullable(),
  quantity: z.number().nullable(),
  subtotal: z.number().nullable(),
}).passthrough();

const EmptyObjectSchema = z.object({}).passthrough();

// ============================================================
// Exported types
// ============================================================

export type InboundListItem = z.infer<typeof InboundListItemSchema>;
export type InboundTableRow = z.infer<typeof InboundTableRowSchema>;
export type InboundDetail = z.infer<typeof InboundDetailSchema>;
export type InboundItemDetail = z.infer<typeof InboundItemDetailSchema>;
export type InboundReceiptSummary = z.infer<typeof InboundReceiptSummarySchema>;
export type InboundReceiptItemFull = z.infer<typeof InboundReceiptItemFullSchema>;
export type InboundHistoryEntry = z.infer<typeof InboundHistorySchema>;
export type InboundVasEntry = z.infer<typeof InboundVasEntrySchema>;
export type InboundReturnOrderRef = z.infer<typeof InboundReturnOrderRefSchema>;
export type InboundActiveReceipt = z.infer<typeof InboundActiveReceiptSchema>;
export type InboundBasic = z.infer<typeof InboundBasicSchema>;

// ============================================================
// Service
// ============================================================

const inboundService = {
  // 列表 (GET) - 用於搜尋、Landing 頁面
  fetchList: async (params: Record<string, unknown> = {}) => {
    const response = await operatorApiFetch('inbounds', {
      method: 'GET',
      params: params,
    });
    return parseApiResponse(response, apiList(InboundListItemSchema));
  },

  // 獲取入庫單列表（用於表格）
  getRecords: async (params: Record<string, unknown> = {}) => {
    const res = await operatorApiFetch('inbounds/get_records', {
      method: 'POST',
      body: params,
    });
    return parseApiResponse(res, apiList(InboundTableRowSchema));
  },

  // 獲取所有入庫單 ID（用於篩選）
  getIds: async (params: Record<string, unknown> = {}) => {
    const res = await operatorApiFetch('inbounds/get_ids', {
      method: 'POST',
      body: params,
    });
    return parseApiResponse(res, apiSuccess(z.array(z.string())));
  },

  // 獲取單個入庫單
  get: async (id: string) => {
    const res = await operatorApiFetch(`inbounds/${id}`, {
      method: 'GET',
    });
    return parseApiResponse(res, apiSuccess(InboundDetailSchema));
  },

  // 獲取單個入庫單（別名）
  getById: async (id: string) => {
    return inboundService.get(id);
  },

  // 創建入庫單
  create: async (data: Record<string, unknown>) => {
    const res = await operatorApiFetch('inbounds', {
      method: 'POST',
      body: { inbound: data },
    });
    return parseApiResponse(res, apiSuccess(InboundBasicSchema));
  },

  // 更新入庫單
  update: async (id: string, data: Record<string, unknown>) => {
    const res = await operatorApiFetch(`inbounds/${id}`, {
      method: 'PUT',
      body: { inbound: data },
    });
    return parseApiResponse(res, apiSuccess(InboundBasicSchema));
  },

  // 取消入庫單
  cancel: async (id: string) => {
    const res = await operatorApiFetch(`inbounds/${id}/cancel`, {
      method: 'POST',
    });
    return parseApiResponse(res, apiSuccess(InboundBasicSchema));
  },

  // 恢復入庫單
  resume: async (id: string) => {
    const res = await operatorApiFetch(`inbounds/${id}/resume`, {
      method: 'POST',
    });
    return parseApiResponse(res, apiSuccess(InboundBasicSchema));
  },

  // 提前結案（關閉驗收）
  closeReceiving: async (id: string) => {
    const res = await operatorApiFetch(`inbounds/${id}/close_receiving`, {
      method: 'POST',
    });
    return parseApiResponse(res, apiSuccess(CloseReceivingResultSchema));
  },

  // 匯出入庫單
  export: async (ids: string[]) => {
    const res = await operatorApiFetch('inbounds/export', {
      method: 'POST',
      body: { ids },
    });
    return parseApiResponse(res, apiSuccess(JobIdResultSchema));
  },

  // 匯入入庫單
  import: async (formData: FormData) => {
    const response = await operatorApiFetch('inbounds/import', {
      method: 'POST',
      body: formData,
    });
    return parseApiResponse(response, apiSuccess(ImportResultSchema));
  },

  // 下載匯入模板
  // 回傳 raw Response（binary xlsx），不走 envelope parse
  downloadImportTemplate: async (locale: string, merchantId?: string): Promise<Response> => {
    const body: { type: string; locale: string; merchant_id?: string } = { type: 'inbound', locale };
    if (merchantId) {
      body.merchant_id = merchantId;
    }
    return operatorApiFetch('import_templates/download', {
      method: 'POST',
      body: JSON.stringify(body),
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      },
    });
  },

  // 產生 PDF
  generatePdf: async (id: string) => {
    const res = await operatorApiFetch(`inbounds/${id}/generate_pdf`, {
      method: 'POST',
    });
    return parseApiResponse(res, apiSuccess(JobIdResultSchema));
  },

  // 產生標籤
  generateLabels: async (id: string) => {
    const res = await operatorApiFetch(`inbounds/${id}/generate_labels`, {
      method: 'POST',
    });
    return parseApiResponse(res, apiSuccess(JobIdResultSchema));
  },

  // 加值服務 - 新增
  addValueAddedService: async (inboundId: string, data: Record<string, unknown>) => {
    const res = await operatorApiFetch(`inbounds/${inboundId}/value_added_services`, {
      method: 'POST',
      body: { value_added_service: data },
    });
    return parseApiResponse(res, apiSuccess(VasServiceResultSchema));
  },

  // 加值服務 - 更新
  updateValueAddedService: async (inboundId: string, serviceId: string, data: Record<string, unknown>) => {
    const res = await operatorApiFetch(`inbounds/${inboundId}/value_added_services/${serviceId}`, {
      method: 'PATCH',
      body: { value_added_service: data },
    });
    return parseApiResponse(res, apiSuccess(VasServiceResultSchema));
  },

  // 加值服務 - 刪除
  deleteValueAddedService: async (inboundId: string, serviceId: string) => {
    const res = await operatorApiFetch(`inbounds/${inboundId}/value_added_services/${serviceId}`, {
      method: 'DELETE',
    });
    return parseApiResponse(res, apiSuccess(EmptyObjectSchema));
  },
};

export { inboundService };
export default inboundService;
